<script setup lang="ts">
import { GridItem, GridLayout } from "grid-layout-plus";
import { computed, onBeforeUnmount, ref, watch, onMounted } from "vue";
import Card from "../components/Card.vue";
import Banner from "@/modules/dashboards/components/Banner.vue";
import PageBreakIndicator from "../components/PageBreakIndicator.vue";
import { useDashboardStore } from "@/modules/dashboards/store";
import {
	a4PageHeaderInPx,
	a4PageHeightInPx,
	DashboardLayout,
	DashboardLayoutItem,
} from "@/modules/dashboards/pages/DashboardTypes";
import {
	canPrintDashboard,
	ROWS_PER_PAGE,
} from "@/modules/dashboards/dashboard-utils";
import {
	displaceCardsOnDrag,
	settleCardAfterDrop,
	takeDragSnapshot,
	DragSnapshot,
} from "@/modules/dashboards/card-displacement";
import DashboardCardDO = App.Data.DashboardCardDO;
import { useExplorerSecondaryDrawer } from "@/modules/explorer/composables/ExplorerSecondaryDrawer";
import DashboardContextMenu from "@/modules/dashboards/components/DashboardContextMenu.vue";
import useDialog from "@/ui/composables/useDialog";
import router from "@/router/router";
import { useApiClient } from "@/api/client";
import useToast from "@/ui/composables/useToast";
import { useExplorerStore } from "@/modules/explorer/store";
const { openSecondaryDrawer } = useExplorerSecondaryDrawer();

const dashboardState = useDashboardStore().dashboardState;
const gridRef = ref<HTMLElement | null>(null);
const isContextMenuVisible = ref(false);
const contextMenuClickedEvent = ref<MouseEvent | null>(null);
const clickedCard = ref<DashboardLayoutItem | null>(null);
const clickedCardFull = ref<DashboardCardDO | null>(null);

const stageRef = ref<HTMLElement | null>(null);
const stageContentRef = ref<HTMLElement | null>(null);
const stageWidth = ref(0);
const stageContentWidth = ref(0);
let stageResizeObserver: ResizeObserver;
let stageContentResizeObserver: ResizeObserver;

type CardExpose = { openDownloadMenu: () => void };
const cardRefs = ref<Record<string, CardExpose | null>>({});

const justifyStageContent = computed(() =>
	stageContentWidth.value * dashboardState.previewZoomFactor > stageWidth.value
		? "start"
		: "center"
);

onMounted(() => {
	if (stageRef.value) {
		stageResizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				stageWidth.value = entry.contentRect.width;
			}
		});
		stageResizeObserver.observe(stageRef.value);
	}
	if (stageContentRef.value) {
		stageContentResizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				stageContentWidth.value = entry.contentRect.width;
			}
		});
		stageContentResizeObserver.observe(stageContentRef.value);
	}
});

onBeforeUnmount(() => {
	stageResizeObserver.disconnect();
	stageContentResizeObserver.disconnect();
});

const printMode = computed(() => dashboardState.dashboard.mode === "print");
const isEditMode = computed(() => dashboardState.editorMode === "edit");
const isPrintable = computed(() => {
	return canPrintDashboard(dashboardState.dashboard);
});

const baseSizeViewport = 720;

const a4scale = 0.5;

// 12 is the card padding in printMode, 24 is the padding in web mode and there is a 1px border
const cardPadding = (printMode.value ? 12 + 1 : 24 + 1) * 2;
const cardMargin = 10 * a4scale;
const rowHeight =
	((a4PageHeightInPx - a4PageHeaderInPx) / ROWS_PER_PAGE) * a4scale -
	cardMargin;

const rowHeightIncludingMargin = rowHeight + cardMargin;

const layout = computed<DashboardLayout>(() =>
	dashboardState.dashboard.cards.map(
		(card) => card.layout as DashboardLayoutItem
	)
);

const cardsFinishedLoading = ref(0);

onBeforeUnmount(() => {
	dashboardState.editorMode = "view";
	dashboardState.isLive = false;
});

watch(dashboardState.dashboard.cards, () => {}, { deep: true });
watch(printMode, () => {
	dashboardState.previewZoomFactor = 1.0;
});

async function deleteCard(card: DashboardLayoutItem) {
	const confirm = await useDialog().confirm({
		title: "Confirm delete",
		message: `Are you sure you want to delete this card?`,
		isDelete: true,
	});

	if (!confirm) return false;

	dashboardState.dashboard.cards = dashboardState.dashboard.cards.filter(
		(c) => c.layout.i !== card.i
	);
}

const confirmLeavingDashboard = async (card: DashboardCardDO) => {
	if (!card?.id && dashboardState.dashboardHasChanges) {
		await useDialog().alert({
			title: "Save changes first..",
			message:
				"Save your dashboard changes before modifying this card's configuration. Otherwise, leaving the dashboard will remove this unsaved card.",
		});
		return false;
	}

	if (dashboardState.dashboardHasChanges) {
		return await useDialog().confirm({
			title: "Unsaved changes",
			message:
				"Are you sure you want to exit this dashboard? All unsaved changes will be lost.",
		});
	}

	return true;
};

const explorerRouteNameForCard = (card: DashboardCardDO) =>
	card.type === "table"
		? "explorer.metricWrapper"
		: card.type === "graph"
		? "explorer.graphWrapper"
		: "explorer.singleMetricWrapper";

// Opens the card as shown on the dashboard (dashboard filters and dates applied)
const viewCardInExplorer = async (card: DashboardCardDO) => {
	if (!(await confirmLeavingDashboard(card))) return;

	const response = await useApiClient("dashboard").getNormalizedDocument(
		dashboardState.dashboard,
		card.uuid
	);
	if (response.isErr()) {
		useToast().error("Unable to open this card in the explorer");
		return;
	}

	useExplorerStore().explorerState.dashboardCardDocumentToView = response.value;
	router.push({ name: explorerRouteNameForCard(card) });
};

// Opens the card's own stored document without the dashboard filters, so it
// can be changed and saved, after which the explorer offers to return here.
const editCardInExplorer = async (card: DashboardCardDO) => {
	if (!(await confirmLeavingDashboard(card))) return;

	router.push({
		name: explorerRouteNameForCard(card),
		query: {
			"base-document": card.stored_document?.active_document_uuid ?? "",
			dashboard: dashboardState.dashboard.uuid,
		},
	});
};
const openContextMenu = (event: MouseEvent, item: DashboardLayoutItem) => {
	clickedCard.value = item;
	clickedCardFull.value =
		dashboardState.dashboard.cards.find((c) => c.layout.i === item.i) ?? null;
	contextMenuClickedEvent.value = event;
	isContextMenuVisible.value = true;
};
const showCantPrint = computed(() => {
	if (printMode.value || isEditMode.value) {
		return false;
	}

	return !canPrintDashboard(dashboardState.dashboard);
});

const setCardHeight = (cardIndex: number, height: number) => {
	if (printMode.value) {
		height /= 2;
	}

	dashboardState.dashboard.cards[cardIndex].layout.h = Math.ceil(
		(height + cardPadding + cardMargin) / rowHeightIncludingMargin
	);
};

const editCard = (card: DashboardCardDO) => {
	dashboardState.cardToEdit = card;
	openSecondaryDrawer("AddEditCard");
};

const editCardByLayoutItem = (item: DashboardLayoutItem) => {
	const card = dashboardState.dashboard.cards.find(
		(c) => c.layout.i === item.i
	);
	if (card) {
		editCard(card);
	}
};

const findCardByLayoutItem = (item: DashboardLayoutItem) =>
	dashboardState.dashboard.cards.find((c) => c.layout.i === item.i);

const viewCardInExplorerByLayoutItem = (item: DashboardLayoutItem) => {
	const card = findCardByLayoutItem(item);
	if (card) {
		viewCardInExplorer(card);
	}
};

const editCardInExplorerByLayoutItem = (item: DashboardLayoutItem) => {
	const card = findCardByLayoutItem(item);
	if (card) {
		editCardInExplorer(card);
	}
};

const downloadCard = (item: DashboardLayoutItem) => {
	const card = dashboardState.dashboard.cards.find(
		(c) => c.layout.i === item.i
	);
	if (card) {
		cardRefs.value[card.layout.i]?.openDownloadMenu();
	}
};

const addNewCard = () => {
	openSecondaryDrawer("AddEditCard");
};

const openAiReportDrawer = () => {
	openSecondaryDrawer("AiReport");
};

const triggerDashboardChanged = () => {
	dashboardState.dashboardHasChanges = true;
};

let dragSnapshot: DragSnapshot | null = null;
let draggedCardId: number | string | null = null;

const onCardMove = (index: number, x: number, y: number) => {
	if (dragSnapshot === null || draggedCardId !== index) {
		draggedCardId = index;
		dragSnapshot = takeDragSnapshot(layout.value);
	}
	displaceCardsOnDrag(layout.value, index, { x, y }, dragSnapshot);
};

const onCardMoved = (index: number, x: number, y: number) => {
	const snapshot = dragSnapshot;
	dragSnapshot = null;
	draggedCardId = null;
	if (snapshot) {
		settleCardAfterDrop(layout.value, index, { x, y }, snapshot);
	}
	triggerDashboardChanged();
};

const getCardHeight = computed(() => (h: number) => {
	let height = rowHeightIncludingMargin * h - cardPadding - cardMargin;
	if (printMode.value) {
		height *= 2;
	}
	return height;
});
</script>

<template>
	<DashboardContextMenu
		v-if="isContextMenuVisible"
		v-model="isContextMenuVisible"
		:layout="layout"
		:clickedCard="clickedCard"
		:clickedCardFull="clickedCardFull"
		:position="
			contextMenuClickedEvent
				? {
						x: contextMenuClickedEvent.clientX,
						y: contextMenuClickedEvent.clientY,
				  }
				: undefined
		"
		@delete="deleteCard"
		@edit="editCardByLayoutItem"
		@view-in-explorer="viewCardInExplorerByLayoutItem"
		@edit-in-explorer="editCardInExplorerByLayoutItem"
		@download-card="downloadCard"
	/>

	<v-alert
		v-if="dashboardState.dashboard.ai_settings?.enabled"
		title="Ai Rapport Enabled"
		type="info"
		border="start"
		variant="tonal"
		text="This dashboard will be exported with an Ai Rapport"
		class="mt-1 alert text-caption"
	>
	</v-alert>
	<div
		class="stage"
		ref="stageRef"
		:style="{ justifyContent: justifyStageContent }"
	>
		<v-alert
			v-if="!layout.length"
			title="Configuring Dashboards"
			type="info"
			border="start"
			variant="tonal"
		>
			<p>
				To add cards to your dashboard, click the
				<v-icon>fa: fa-duotone fa-grid-2-plus</v-icon> button on the bottom of
				this page.
			</p>
		</v-alert>

		<div
			v-show="layout.length"
			class="content"
			ref="stageContentRef"
			:style="
				printMode
					? {
							width: `${baseSizeViewport}px`,
							zoom: dashboardState.previewZoomFactor,
					  }
					: { width: `100%` }
			"
		>
			<v-alert
				v-if="!isPrintable && !isEditMode && printMode"
				title="Dashboard not printable"
				type="error"
				variant="tonal"
				class="my-2 alert text-caption"
				:data-a4="printMode"
				border="start"
			>
				<p>
					There are cards overlapping a page break. Please adjust the layout
					such that every card fits on a page.<br />
					Enable 'Edit layout' ( <v-icon>mdi-resize</v-icon> in top menu ) to
					preview the page-breaks.
				</p>
			</v-alert>

			<div v-if="layout.length" class="dashboard-container">
				<div
					class="page"
					:data-fullscreen="dashboardState.isFullscreen"
					:data-edit="isEditMode"
					:data-a4="printMode"
					style="margin-bottom: 500px"
				>
					<div class="a4" ref="gridRef">
						<Banner v-if="dashboardState.isFullscreen" />
						<GridLayout
							v-model:layout="layout"
							:col-num="12"
							:margin="[10 * a4scale, 10 * a4scale]"
							:key="dashboardState.dashboard.uuid"
							:row-height="rowHeight"
							:is-draggable="isEditMode"
							:is-resizable="isEditMode"
							:is-bounded="false"
							:prevent-collision="true"
							:vertical-compact="false"
						>
							<GridItem
								@resized="() => triggerDashboardChanged()"
								@move="(i, x, y) => onCardMove(i, x, y)"
								@moved="(i, x, y) => onCardMoved(i, x, y)"
								@contextmenu.prevent="(event) => openContextMenu(event, item)"
								v-for="(item, i) in layout"
								:key="item.i"
								:x="item.x"
								:y="item.y"
								:w="item.w"
								:h="item.h"
								:i="item.i"
								:max-h="item.maxH"
								:min-h="2"
								:is-resizable="
									isEditMode &&
									dashboardState.dashboard.cards[i].use_card_layout_height
								"
							>
								<Card
									:key="
										dashboardState.dashboard.cards[i]?.uuid ??
										dashboardState.dashboard.cards[i].id
									"
									:ref="(el) => (cardRefs[item.i] = el as unknown as CardExpose | null)"
									class="avoid-print-break"
									:card="dashboardState.dashboard.cards[i]"
									:is-pdf-render="false"
									:height="getCardHeight(item.h)"
									:row-height="rowHeightIncludingMargin"
									@finished-loading="() => cardsFinishedLoading++"
									@set-card-height="(height) => setCardHeight(i, height)"
									@edit-card="editCard(dashboardState.dashboard.cards[i])"
								/>
							</GridItem>
						</GridLayout>
					</div>
				</div>

				<!-- Replace the page break indicators with the new component -->
				<PageBreakIndicator
					v-if="dashboardState.editorMode === 'edit'"
					:grid-ref="gridRef"
					:layout="layout"
					:is-a4="printMode"
					:scale="a4scale"
				/>
			</div>
		</div>
	</div>

	<v-fab
		:disabled="!useDashboardStore().userCanEdit"
		@click="addNewCard"
		:absolute="false"
		:app="true"
		location="bottom end"
		size="large"
		icon="fa: fa-duotone fa-grid-2-plus"
		fixed
		data-testid="add-dashboard-card"
	/>
	<!--	<v-fab-->
	<!--		@click="openAiReportDrawer"-->
	<!--		:app="true"-->
	<!--		:disabled="dashboardState.dashboardHasChanges"-->
	<!--		location="bottom end"-->
	<!--		size="large"-->
	<!--		icon="fa: fa-duotone fa-brain"-->
	<!--		fixed-->
	<!--		style="bottom: 75px"-->
	<!--	/>-->
</template>

<style scoped lang="scss">
// Your existing styles without the page-breaks related CSS
$scale: 0.5;

@media print {
	.avoid-print-break {
		page-break-inside: avoid;
	}
}

.alert {
	&[data-a4="true"] {
		width: 1440px * $scale;
		margin: 0 auto;
	}
}

.page {
	width: 100%;
	min-height: 50%;
	border-radius: 0.5rem;

	&[data-a4="true"] {
		width: 1440px * $scale;
		margin: 0 auto;
	}

	&[data-edit="true"] {
		--grid-columns: 12;
		--row-height: 160px * $scale;
		--grid-gap: 10px * $scale;

		border: 1px dashed rgb(164, 164, 164);
		background-color: rgba(77, 120, 165, 0.1);
		background-image: linear-gradient(
				to right,
				rgba(255, 255, 255, 1) 1px,
				transparent 1px
			),
			linear-gradient(to bottom, rgba(255, 255, 255, 1) 1px, transparent 1px);
		background-repeat: repeat;
		background-size: calc(100% / var(--grid-columns))
			calc(var(--row-height) + var(--grid-gap));
	}

	&[data-fullscreen="true"] {
		position: absolute;
		width: 100dvw;
		height: 100dvh;
		top: 0;
		left: 0;
		z-index: 99999;
		background: white;
		print-color-adjust: economy;
		display: flex;
		justify-content: center;
	}
}

.vgl-layout {
	height: 100%;
	border-radius: 1rem;
}

:deep(.vgl-item) {
	user-select: none;
	border-radius: 1rem;
}

:deep(.vgl-item:is(.vgl-item--placeholder)) {
	background-color: rgba(230, 97, 97, 0.55);
}

:deep(.vgl-item--resizing) {
	opacity: 90%;
}

:deep(.vgl-item--static) {
	background-color: #cce;
}

.dashboard-container {
	display: flex;
	gap: 1rem;
	align-items: flex-start;
}

/* stage provides the scrollable layout area */
.stage {
	position: relative;
	overflow-x: auto;
	width: 100%;
	display: flex;
}
</style>
